import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
} from '@angular/core';

import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { finalize, Observable, of, Subject, take, takeUntil } from 'rxjs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import {
  ApiRespons,
  ManageOrganizationDrawerParams,
  Title,
  UserModel,
} from '../../../manage-organization.interface';
import {
  OrgManagementMode,
  UserStatus,
} from '../../../manage-organization.enums';
import { ManageOrganizationService } from '../../../manage-organization.service';
import { OrganizationService } from '../../../../organizations/organization.service';
import { CommonService } from '../../../../../shared/services/common.service';
import { Role } from '../../../../../shared/services/role.service';
import { LookupItem } from '../../../../../core/models/lookup.models';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { LoadingContentComponent } from '../../../../../shared/loading-content/loading-content.component';
import { LoaderService } from '../../../../../core/services/loader.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';

@Component({
  selector: 'app-manually',
  standalone: true,
  imports: [
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormControlComponent,
    NzFormLabelComponent,
    CommonModule,
    NzInputModule,
    NzSelectModule,
    ReactiveFormsModule,
    NzButtonModule,
    CommonModule,
    TitleCasePipe,
    LoadingContentComponent,
    NzSpinModule,
  ],
  templateUrl: './manually.component.html',
  styleUrl: './manually.component.scss',
})
export class ManuallyComponent implements OnInit, OnDestroy {
  @Input() roleList: Observable<Role[]> | null = null;
  @Input() isLoading!: Observable<boolean>;
  titleList: Title[] = [];
  statusList: LookupItem[] = [];

  organizationUserForm!: FormGroup;
  loading = false;
  orgParams: ManageOrganizationDrawerParams = {
    organizationId: 0,
    mode: OrgManagementMode.CREATE,
  };
  errorMessage = '';
  loading$: Observable<boolean> = of(false);
  private readonly destroy$ = new Subject<void>();
  mappedRoles: number[] = [];
  isDisableAllCheckBox = false;
  private readonly formControls = {
    id: [null],
    firstName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern('^[a-zA-Z][a-zA-Z\\s]*[a-zA-Z]$'),
        this.filledValue,
      ],
    ],
    lastName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        Validators.pattern('^[a-zA-Z][a-zA-Z\\s]*[a-zA-Z]$'),
        this.filledValue,
      ],
    ],
    titleId: ['', [Validators.required, Validators.maxLength(100)]],
    email: [
      '',
      [
        Validators.required,
        Validators.email,
        Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'),
      ],
    ],
    roleIds: [[], [Validators.required]],
    statusId: ['', [Validators.required]],
  };
  constructor(
    private readonly fb: FormBuilder,
    private readonly drawerService: DrawerService,
    private readonly manageOrgService: ManageOrganizationService,
    private readonly commonService: CommonService,
    private readonly cd: ChangeDetectorRef,
    private readonly loaderService: LoaderService,
    private readonly auditContextService: AuditContextService,
    private readonly organizationService: OrganizationService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
    this.organizationUserForm = this.initForm();
  }

  async ngOnInit() {
    await this.getMasterData();
  }

  filledValue(event: FormControl) {
    return event.value && !event.value.trim() ? { empty: true } : null;
  }

  async getMasterData() {
    await Promise.all([
      this.manageOrgService
        .getTitles({
          organizationId: this.orgParams.organizationId,
        })
        .toPromise(),
      this.commonService.getDataByType('status').toPromise(),
    ])
      .then(
        ([titleList, statusList]: [
          ApiRespons<Title> | undefined,
          ApiRespons<LookupItem> | undefined,
        ]) => {
          this.titleList = titleList ? titleList.result : [];
          this.statusList = statusList
            ? statusList.result.filter(
                s =>
                  s.value !== UserStatus.DELETED &&
                  s.value !== UserStatus.ACTIVE
              )
            : [];
          const defaultStatus = this.statusList.find(
            s => s.value === UserStatus.PENDING
          );
          this.organizationUserForm.controls['statusId'].setValue(
            defaultStatus?.id || ''
          );
        }
      )
      .catch(e => console.error(e));
  }

  onTitleChange(titleId: number) {
    const selectedTitle = this.titleList.find(t => t.id === titleId);
    if (selectedTitle?.isBaseLined) {
      this.loadRolesMappedToTitles(titleId);
    } else {
      this.isDisableAllCheckBox = false;
      this.organizationUserForm.patchValue({
        roleIds: [],
      });
      this.cd.detectChanges();
    }
  }

  loadRolesMappedToTitles(titleId: number): void {
    this.loaderService.show();
    this.manageOrgService
      .getMappedRolesToTitle(titleId)
      .pipe(
        finalize(() => this.loaderService.hide()),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: res => {
          if (res.result) {
            this.mappedRoles = res.result;
            this.isDisableAllCheckBox =
              this.mappedRoles.length === 0 ? false : true;
            this.organizationUserForm.patchValue({
              roleIds: this.mappedRoles,
            });
          }
          this.loaderService.hide();
          this.cd.detectChanges();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  private initForm(): FormGroup {
    return this.fb.group(this.formControls);
  }

  private createPayload(): UserModel {
    return {
      organizationId: Number(this.orgParams.organizationId),
      organizationIds: [this.orgParams.organizationId],
      ...this.organizationUserForm.value,
    };
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ManageOrganizationDrawerParams>(DRAWER_IDS.MANAGE_ORG)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.orgParams = params;
        }
      });
  }

  async onSubmit() {
    this.loading = true;
    if (this.organizationUserForm.invalid) {
      return;
    }
    const formData = this.createPayload();

    // Build enriched data for audit logging
    const enriched = await this.buildEnrichedUserData(formData);
    const modifiedFields = this.auditContextService.buildFieldChanges(
      {},
      enriched,
      {
        sensitiveFields: ['email', 'password'],
        labelMap: this.buildFieldLabelMap(),
        excludeFields: [
          'id',
          'createdDate',
          'updatedDate',
          'createdBy',
          'updatedBy',
          'organizationIds',
        ],
      }
    );
    // Optionally: pass modifiedFields to backend if API supports it

    this.manageOrgService
      .createOrgUser({ ...formData, _modifiedFields: modifiedFields })
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        next: () => {
          this.handleSubmissionSuccess();
        },
        error: (error: Error) => {
          this.handleError(error);
        },
      });
  }
  private handleSubmissionSuccess(): void {
    this.showNotification();
    this.closeDrawer();
  }

  private showNotification(): void {
    this.commonService.notify('success', 'User has been created successfully');
  }
  private async buildEnrichedUserData(
    formData: UserModel
  ): Promise<Record<string, unknown>> {
    const orgId = Array.isArray(formData.organizationId)
      ? formData.organizationId[0]
      : formData.organizationId;
    return {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      statusName: this.getStatusName(formData.statusId),
      organizationName: await this.getOrganizationName(orgId),
      titleName: this.getTitleName(formData.titleId),
      roleNames: this.getRoleNames(formData.roleIds),
    };
  }

  private getStatusName(statusId: number): string {
    const status = this.statusList.find(s => s.id === statusId);
    return status?.value || '';
  }

  private orgNameCache: Record<number, string> = {};
  async getOrganizationName(orgId: number): Promise<string> {
    if (!orgId) return '';
    if (this.orgNameCache[orgId]) return this.orgNameCache[orgId];
    try {
      const org = await this.organizationService
        .getOrganization(orgId)
        .pipe(take(1))
        .toPromise();
      const name = org?.organizationName || '';
      this.orgNameCache[orgId] = name;
      return name;
    } catch {
      return '';
    }
  }

  private getTitleName(titleId: number): string {
    const title = this.titleList.find(t => t.id === titleId);
    return title?.title || '';
  }

  private getRoleNames(roleIds: number[]): string {
    if (!this.roleList) return '';
    let roles: Role[] = [];
    this.roleList
      ?.pipe(take(1), takeUntil(this.destroy$))
      .subscribe(list => (roles = list));
    return roles
      .filter(role => roleIds.includes(role.id))
      .map(role => role.name)
      .join(', ');
  }

  private buildFieldLabelMap(): Record<string, string> {
    return {
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      statusName: 'Status',
      organizationName: 'Organization',
      titleName: 'Title',
      roleNames: 'Roles',
    };
  }

  private handleError(error: Error): void {
    this.loading = false;
    this.cd.detectChanges();
    this.commonService.notify('error', error.message);
  }

  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.USER);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
